import { useCallback, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { hasSupabaseConfig, signInWithPassword, signUpWithPassword, supabase } from "../lib/supabase";

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(hasSupabaseConfig);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return undefined;
    }

    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const requestPasswordSignIn = useCallback(async (email: string, password: string) => {
    if (isSubmitting) return;

    setMessage(null);
    setError(null);
    setIsSubmitting(true);

    try {
      await signInWithPassword(email, password);
      setMessage(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in.");
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting]);

  const requestPasswordSignUp = useCallback(async (email: string, password: string) => {
    if (isSubmitting) return;

    setMessage(null);
    setError(null);
    setIsSubmitting(true);

    try {
      const data = await signUpWithPassword(email, password);
      if (data.session) {
        setMessage("Account created and signed in.");
      } else {
        setMessage("Account created. Check your email to confirm, then log in.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create account.");
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting]);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setMessage(null);
    setError(null);
    setIsSubmitting(false);
  }, []);

  return {
    session,
    user: session?.user ?? null,
    loading,
    message,
    error,
    isSubmitting,
    isConfigured: hasSupabaseConfig,
    requestPasswordSignIn,
    requestPasswordSignUp,
    signOut,
  };
}
